import { useEffect, useCallback } from "react";
import { Platform } from "react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Constants from "expo-constants";
import axios from "axios";
import { VERSION_CHECK_QUERY_KEY } from "@/app/config/queryClient";

type VersionResponse = {
  version: string;
};

const VERSION_FILE_PATH = "/version.json";

async function fetchVersion(): Promise<string | null> {
  const response = await axios.get<VersionResponse>(
    `${VERSION_FILE_PATH}?t=${Date.now()}`,
  );
  return response.data.version;
}

export function useVersionCheck() {
  const queryClient = useQueryClient();

  const currentSha = Constants.expoConfig?.extra?.gitSha as string | undefined;
  const isDevelopment = !currentSha || currentSha === "development";
  const isWeb = Platform.OS === "web";
  const enabled = isWeb && !isDevelopment;

  const { data: latestVersion } = useQuery({
    queryKey: VERSION_CHECK_QUERY_KEY,
    queryFn: fetchVersion,
    enabled,
  });

  const isUpdateAvailable =
    enabled && latestVersion != null && latestVersion !== currentSha;

  const triggerCheck = useCallback(() => {
    if (enabled) {
      void queryClient.invalidateQueries({ queryKey: VERSION_CHECK_QUERY_KEY });
    }
  }, [enabled, queryClient]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const DAILY_CHECK_HOUR = 7;

    const getMsUntil7AM = (): number => {
      const now = new Date();
      const next7AM = new Date();
      next7AM.setHours(DAILY_CHECK_HOUR, 0, 0, 0);

      if (now >= next7AM) {
        next7AM.setDate(next7AM.getDate() + 1);
      }

      return next7AM.getTime() - now.getTime();
    };

    const scheduleDailyCheck = (): NodeJS.Timeout => {
      return setTimeout(() => {
        triggerCheck();
        timeoutId = scheduleDailyCheck();
      }, getMsUntil7AM());
    };

    let timeoutId = scheduleDailyCheck();

    return () => clearTimeout(timeoutId);
  }, [enabled, triggerCheck]);

  return {
    isUpdateAvailable,
    triggerCheck,
    refresh,
  };
}
